import Loading from "@/common/components/ui/loading/Loading";
import { Suspense, useState } from "react";
import GenericTable from "@/common/components/ui/table/GenericTable";
import { TableFilters, TableFiltersState } from "@/common/components/ui/table/TableFilters";
import { useDeleteCampaign, useGetAllCampaigns } from "@/common/hooks";
import { CAMPAING_STATUS, ICampaign } from "@/types/campaign.type";
import { FormCampaign } from "../components/modals/FormCampaign";
import { Modal } from "flowbite-react";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { formatDateAndTime } from "@/common/helpers/date.helpers";
import { useCampaignFilterStore, useCampaignFilterStoreActions } from "@/common/states/store/campaignFilterStore";

const headers = {
  name: "Nome",
  date: "Data de Envio",
  targetAudience: "Público-Alvo",
	audienceType: 'Tipo',
  status: "Status",
  sentCount: "Contatos Atingidos",
};

const STATUS_COLORS = {
  [CAMPAING_STATUS.sent]: "bg-greenPrimary text-white",
  [CAMPAING_STATUS.sending]: "bg-gray text-white",
  [CAMPAING_STATUS.pending]: "bg-yellowPrimary text-white",
  [CAMPAING_STATUS.failed]: "bg-redPrimary text-white",
};

const AUDIENCE_TYPE = {
	renters: 'Locatarios', 
	leads: 'Leads'
}


const getStatusLabelComponent = (status: string) => {
  return (
    <span
      className={`inline-block px-2 py-1 rounded-md text-sm whitespace-nowrap ${STATUS_COLORS[status] || "bg-gray-400"
        }`}
      title={CAMPAING_STATUS[status] || status}
    >
      {CAMPAING_STATUS[status] || status}
    </span>
  );
}

export default function CampaignsTable() {
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>({ eventName: "", data: undefined });
  const [campaignToDelete, setCampaignToDelete] = useState<ICampaign | null>(null);

  const {  pageOptions } = useCampaignFilterStore();
  const { setSearchTerm } = useCampaignFilterStoreActions();

  const { data: campaigns } = useGetAllCampaigns(pageOptions);
  const { mutate: deleteCampaign, isPending: isDeleting } = useDeleteCampaign();

  const handleClose = () => {
    setAction({ eventName: "", data: undefined });
    setCampaignToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (campaignToDelete) {
      deleteCampaign(campaignToDelete._id);
      handleClose();
    }
  };

  const actions = [
    {
      label: "Editar",
      action: (data: unknown) =>
        setAction({ eventName: "edit", data: data as ICampaign }),
    },
    {
      label: "Deletar",
      action: (data: unknown) => {
        setCampaignToDelete(data as ICampaign);
        setAction({ eventName: "delete", data: data as ICampaign });
      },
    },
  ];

  return (
     <Suspense fallback={<Loading />}>
       <div className="table-section">
         <TableFilters
          statusOptions={CAMPAING_STATUS}
          dashboardHeader="Disparos de Campanhas"
          placeholder="Digite o nome da campanha"
          newItemLabel="Nova Campanha"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={(value: Partial<TableFiltersState>) => setSearchTerm(value)}
          tableData={campaigns as unknown as Record<string, unknown>[]}
          entity="campaigns"
         />
         <GenericTable
          headers={headers}
          data={campaigns as unknown as Record<string, unknown>[]}
          actions={actions}
					entity="campaigns"
					renders={{
						status: (_, campaign) => 
						getStatusLabelComponent((campaign as Record<string, string>).status),
						date: (_, campaign) => formatDateAndTime((campaign as Record<string, string>).date), 
							audienceType: (_, campaign) => AUDIENCE_TYPE[(campaign as Record<string, string>).audienceType]
					}}
         />
       </div>
       {(action.eventName === "create" || action.eventName === "edit") && (
         <FormCampaign
           isOpen={action.eventName === "create" || action.eventName === "edit"}
           onClose={handleClose}
           initialData={action.data as ICampaign}
         />
       )}

       {campaignToDelete && (
         <Modal show={action.eventName === "delete"} onClose={handleClose}>
           <Modal.Header>Confirmar Exclusão</Modal.Header>
           <Modal.Body>
             <p>Tem certeza que deseja excluir a campanha <b>{campaignToDelete.name}</b>?</p>
           </Modal.Body>
           <Modal.Footer>
             <ButtonWithLoader
               onClick={handleDeleteConfirm}
               isLoading={isDeleting}
             >
               Excluir
             </ButtonWithLoader>
             <ButtonWithLoader onClick={handleClose}>
               Cancelar
             </ButtonWithLoader>
           </Modal.Footer>
         </Modal>
       )}
     </Suspense>
  );
}
